import { useState, useEffect, useCallback } from 'react';
import LetterCanvas from '../components/LetterCanvas';

const API_URL = process.env.REACT_APP_API_URL || '';

function api(path, token, options = {}) {
  return fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...options,
  }).then((r) => r.json());
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default function PhysicalMail({ token }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calendar, setCalendar] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [letters, setLetters] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  const fetchCalendar = useCallback(async () => {
    try {
      const data = await api(`/admin/physical-mail/calendar?month=${monthStr}`, token);
      setCalendar(data.calendar || {});
    } catch (e) {
      console.error(e);
    }
  }, [monthStr, token]);

  useEffect(() => {
    fetchCalendar();
    setSelectedDate(null);
    setLetters([]);
  }, [fetchCalendar]);

  const fetchLetters = async (date) => {
    setSelectedDate(date);
    setLoading(true);
    try {
      const data = await api(`/admin/physical-mail?date=${date}`, token);
      setLetters(data.letters || []);
      setSentCount(data.sentCount || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const markSent = async (letter, index) => {
    if (!window.confirm(`#${index + 1} 편지를 발송 완료 처리하시겠습니까?`)) return;
    try {
      const result = await api('/admin/physical-mail/mark-sent', token, {
        method: 'POST',
        body: JSON.stringify({ PK: letter.PK, SK: letter.SK }),
      });
      if (result.success) {
        setLetters((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], physicalMailStatus: 'SENT' };
          return updated;
        });
        setSentCount((c) => c + 1);
        alert('발송 완료 처리되었습니다.');
      } else {
        alert(result.error || '처리에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  };

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
  };

  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };

  // 달력 그리기
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let week = new Array(firstDay).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  return (
    <div className="physical-mail">
      <div className="calendar-section">
        <div className="calendar-nav">
          <button onClick={prevMonth}>&lt;</button>
          <h2>{year}년 {month}월</h2>
          <button onClick={nextMonth}>&gt;</button>
        </div>

        <table className="calendar-table">
          <thead>
            <tr>
              {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
                <th key={d}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w, wi) => (
              <tr key={wi}>
                {w.map((d, di) => {
                  if (!d) return <td key={di} className="empty" />;
                  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                  const info = calendar[dateStr];
                  const total = info ? info.pending + info.sent : 0;
                  const isSelected = selectedDate === dateStr;
                  return (
                    <td
                      key={di}
                      className={`day ${total > 0 ? 'has-mail' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => total > 0 && fetchLetters(dateStr)}
                    >
                      <div className="day-number">{d}</div>
                      {total > 0 && (
                        <div className="mail-counts">
                          {info.pending > 0 && <span className="mail-count pending">{info.pending}건 대기</span>}
                          {info.sent > 0 && <span className="mail-count sent">{info.sent}건 완료</span>}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDate && (
        <div className="detail-section">
          <div className="detail-header">
            <h3>{formatDate(selectedDate)} 실물 편지 ({letters.length}건)</h3>
            <div className="sent-count-badge">발송완료 {sentCount}건</div>
          </div>
          {loading ? (
            <p>로딩 중...</p>
          ) : letters.length === 0 ? (
            <p>해당 날짜에 발송 예정인 편지가 없습니다.</p>
          ) : (
            <div className="letter-list">
              {letters.map((letter, i) => (
                <div key={i} className="letter-card">
                  <div className="letter-header">
                    <span className="letter-index">#{i + 1}</span>
                    <span className={`letter-status status-${letter.physicalMailStatus?.toLowerCase()}`}>
                      {letter.physicalMailStatus}
                    </span>
                  </div>

                  <div className="letter-info">
                    <div className="info-row">
                      <label>수신자</label>
                      <span>{letter.recipientName || '-'}</span>
                    </div>
                    <div className="info-row">
                      <label>발신자</label>
                      <span>{letter.senderName || '-'}</span>
                    </div>
                    <div className="info-row">
                      <label>수신 주소</label>
                      <span>{letter.recipientAddress || '-'}</span>
                    </div>
                    <div className="info-row">
                      <label>발송일</label>
                      <span>{formatDate(letter.scheduledDate)}</span>
                    </div>
                  </div>

                  <div className="letter-content">
                    <label>편지 내용</label>
                    {(letter.content || []).map((page, pi) => (
                      <div key={pi} className="content-page">
                        <div className="page-label">페이지 {pi + 1}</div>
                        <div className="canvas-row">
                          <LetterCanvas
                            text={page.text || ''}
                            background={letter.letterBackground || '1'}
                            fontFamily={letter.fontFamily || 'NanumMyeongjo'}
                            scale={4}
                            canvasId={`canvas-${i}-${pi}`}
                          />
                          <button
                            className="download-btn"
                            onClick={() => {
                              const imgEl = document.getElementById(`canvas-${i}-${pi}`);
                              if (!imgEl || !imgEl.src) return;
                              const a = document.createElement('a');
                              a.href = imgEl.src;
                              a.download = `letter-${i + 1}-page-${pi + 1}.png`;
                              a.click();
                            }}
                          >
                            PNG 다운로드
                          </button>
                        </div>
                        <details className="text-details">
                          <summary>텍스트 보기</summary>
                          <p className="page-text">{page.text || '(내용 없음)'}</p>
                        </details>
                      </div>
                    ))}
                  </div>

                  {letter.imageUrls && letter.imageUrls.length > 0 && (
                    <div className="letter-images-section">
                      <label>첨부 이미지 ({letter.imageUrls.length}장)</label>
                      <div className="attached-images">
                        {letter.imageUrls.map((url, ii) => (
                          <div key={ii} className="attached-image-item">
                            <a href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt={`첨부 이미지 ${ii + 1}`} className="attached-image" />
                            </a>
                            <button
                              className="download-btn"
                              onClick={() => {
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `letter-${i + 1}-image-${ii + 1}.png`;
                                a.target = '_blank';
                                a.click();
                              }}
                            >
                              이미지 다운로드
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {letter.physicalMailStatus === 'PENDING' && (
                    <button className="mark-sent-btn" onClick={() => markSent(letter, i)}>
                      발송 완료 처리
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
